/**
 * SourceHighlight — inline orange chip that marks a Batemo-cell-sourced value.
 *
 * Renders the value (e.g. "274 Wh/kg") in brand orange with a subtle
 * underlying chip background. On hover, shows a floating tooltip with the
 * full cell name + a "View cell" link. Click navigates to /cells/:slug
 * without closing the chat.
 *
 * Source data is stored in `message.sources[idx]` keyed by the [[SRC:idx]]
 * placeholder index. See chatStore.attachSource.
 *
 * Tooltip positioning uses @floating-ui/dom (already a project dependency).
 */

import { useEffect, useRef, useState } from 'preact/hooks'
import { computePosition, flip, offset, shift } from '@floating-ui/dom'
import { getSlugFromProductId } from '../../utils/cellSlugMap.js'

/**
 * @param {object} props
 * @param {object} props.source — { cellId, value, unit, label }
 * @param {(slug: string) => void} [props.onCellClick] — optional override; defaults to wouter navigation
 */
export function SourceHighlight({ source, onCellClick }) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const tooltipRef = useRef(null)

  if (!source) return null
  const { cellId, value, unit, label } = source

  // Position the tooltip whenever it opens.
  useEffect(() => {
    if (!open || !triggerRef.current || !tooltipRef.current) return

    let cancelled = false
    computePosition(triggerRef.current, tooltipRef.current, {
      placement: 'top',
      middleware: [offset(8), flip(), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      if (cancelled || !tooltipRef.current) return
      Object.assign(tooltipRef.current.style, {
        left: `${x}px`,
        top: `${y}px`,
      })
    })

    return () => { cancelled = true }
  }, [open])

  const display = unit ? `${value} ${unit}` : value

  function handleClick(e) {
    e.preventDefault()
    if (!cellId || cellId.startsWith('_')) return  // synthetic IDs like "_calc"
    const slug = getSlugFromProductId(cellId) || cellId
    if (onCellClick) {
      onCellClick(slug)
    } else if (typeof window !== 'undefined') {
      // Open in same tab — chat panel persists across navigation
      window.location.assign(`/cells/${slug}/factsheet`)
    }
  }

  const isSynthetic = !cellId || cellId.startsWith('_')

  return (
    <span class="ion-src" style={{ position: 'relative', display: 'inline' }}>
      <button
        type="button"
        ref={triggerRef}
        class={`ion-src__chip ${isSynthetic ? 'ion-src__chip--synthetic' : ''}`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={handleClick}
        aria-describedby={open ? `ion-src-tip-${cellId}` : undefined}
      >
        {display}
      </button>
      {open && (
        <span
          ref={tooltipRef}
          id={`ion-src-tip-${cellId}`}
          role="tooltip"
          class="ion-src__tip"
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 1000 }}
        >
          <span class="ion-src__tip-label">
            {isSynthetic ? 'Berechnet' : (label || 'Batemo-Datenpunkt')}
          </span>
          {!isSynthetic && (
            <span class="ion-src__tip-cell">{cellId}</span>
          )}
          {label && !isSynthetic && (
            <span class="ion-src__tip-meta">{label}</span>
          )}
          {!isSynthetic && (
            <span class="ion-src__tip-hint">Klick für Detailseite</span>
          )}
        </span>
      )}
    </span>
  )
}
